<script>
import { authComputed } from '@auth/helpers'
import appConfig from '@src/app.config'

export default {
  name: 'LoginForm',
  props: {
    authError: {
      type: String,
      required: true,
    },
    signUpError: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showLoginPassword: false,
      showPassword: false,
      loginVisible: true,
      signupVisible: false,
      username: '',
      password: '',
      rememberMe: '',
      email: '',
      newPassword: '',
      fullName: '',
      msalInstance: null,
      errors: { email: '', password: '', name: '' },
      prettyAuthErrors: {
        invalid_credentials: 'Invalid Credentials',
        ex_facebook_account:
          'Please use the Forgot Password to set a new password',
        account_not_confirmed:
          'Please check your email and confirm your account first',
        provider_email_taken: 'Email already exists',
      },
      prettySignUpErrors: {
        email_taken: 'Email already exists',
      },
      clientId: appConfig.google_client_id,
      googleParams: {
        client_id: appConfig.google_client_id,
      },
    }
  },
  computed: { ...authComputed },
  mounted() {
    const googleScript = document.createElement('script')
    googleScript.src = 'https://accounts.google.com/gsi/client'
    googleScript.async = true
    googleScript.defer = true
    document.head.appendChild(googleScript)
    var that = this
    googleScript.onload = function() {
      // initialize Google Sign in
      window.google.accounts.id.initialize({
        client_id: appConfig.google_client_id,
        callback: (response) => {
          that.onSuccess(response)
        },
        context: 'signin',
      })

      // render button
      window.google.accounts.id.renderButton(
        document.getElementById('google-one-tap'),
        {
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 140,
        }
      )
    }

    // Load Apple Sign In script
    const appleScript = document.createElement('script')
    appleScript.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    appleScript.async = true
    appleScript.defer = true
    document.head.appendChild(appleScript)

    appleScript.onload = function() {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: appConfig.apple_client_id,
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/apple/callback',
          usePopup: false, // Use redirect instead of popup to avoid blocker
        })
      }
    }
  },
  methods: {
    login() {
      this.$emit('post-login', {
        username: this.username,
        password: this.password,
        rememberMe: this.rememberMe,
      })
    },
    signup() {
      if (!this.email) {
        this.errors.email = 'Email required.'
      } else if (!this.validEmail(this.email)) {
        this.errors.email = 'Valid email required.'
      }
      if (!this.newPassword) {
        this.errors.password = 'Password required.'
      }
      if (!this.fullName) {
        this.errors.name = 'Full Name required.'
      }
      if (!Object.values(this.errors).some((x) => x !== '')) {
        this.$emit('clear-sign-up-errors')
        this.$emit('post-signup', {
          email: this.email,
          newPassword: this.newPassword,
          fullName: this.fullName,
        })
      }
    },
    toggleShowLoginPassword() {
      this.showLoginPassword = !this.showLoginPassword
    },
    validEmail: function(email) {
      const rule = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return rule.test(email)
    },
    clearAuthErrors() {
      this.$emit('clear-auth-errors')
    },
    clearSignUpErrors(fieldName) {
      if (fieldName) {
        this.errors[fieldName] = ''
      }
      this.$emit('clear-sign-up-errors')
    },
    onSuccess(event) {
      const profile = JSON.parse(
        Buffer.from(event.credential.split('.')[1], 'base64').toString()
      )

      this.$emit('post-login-with-provider', {
        name: profile.family_name + ' ' + profile.given_name,
        email: profile.email,
        image_url: profile.picture,
        id_token: event.credential,
        user_id: profile.sub,
        login_provider: 'google',
      })
    },
    onAppleSuccess() {
      if (window.AppleID) {
        // Call signIn directly from user click event to avoid popup blocker
        window.AppleID.auth
          .signIn()
          .then((response) => {
            const { authorization, user } = response

            this.$emit('post-login-with-provider', {
              name: user?.name
                ? `${user.name.firstName} ${user.name.lastName}`
                : '',
              email: user?.email || '',
              id_token: authorization.id_token,
              user_id: authorization.id_token,
              login_provider: 'apple',
            })
          })
          .catch((error) => {
            console.error('Apple Sign In error:', error)
          })
      }
    },
    onMicrosoftSuccess() {
      if (this.msalInstance) {
        const loginRequest = {
          scopes: ['openid', 'profile', 'email', 'User.Read'],
        }

        this.msalInstance
          .loginPopup(loginRequest)
          .then((response) => {
            const account = response.account

            this.$emit('post-login-with-provider', {
              name: account.name || '',
              email: account.username || '',
              id_token: response.idToken,
              user_id: account.homeAccountId,
              login_provider: 'microsoft',
            })
          })
          .catch((error) => {
            console.error('Microsoft Sign In error:', error)
          })
      }
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword
    },
  },
}
</script>

<template>
  <div>
    <h1 class="home-headline1">Let's start sharing kyndfulness</h1>
    <div v-if="loginVisible">
      <div class="mt-8 flex -mx-2">
        <div class="w-1/2 flex-grow pl-2 pr-4 relative">
          <h2 class="home-headline2 mb-5 text-left">Login or Sign Up</h2>
          <span class="relative block">
            <input
              v-model="username"
              type="text"
              name="email"
              placeholder="Username"
              class="input"
              :class="[authError ? 'border-red' : '']"
              @input="clearAuthErrors"
            />
            <svg
              v-if="authError"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
          </span>
        </div>
        <div class="w-1/2 flex-grow pl-4 pr-2 relative">
          <div class="mt-6 mb-6 text-right text-sm leading-relaxed">
            Are you an organization?
            <router-link
              :to="{ name: 'organization-signup' }"
              class="text-sm text-blue"
              >Sign Up Here</router-link
            >
          </div>
          <div class="relative">
            <input
              v-model="password"
              :type="showLoginPassword ? 'text' : 'password'"
              name="password"
              placeholder="Password"
              class="input"
              :class="[authError ? 'border-red' : '']"
              @input="clearAuthErrors"
            />
            <button
              class="focus:outline-none mt-2.5 -ml-8 absolute"
              @click="toggleShowLoginPassword"
            >
              <svg
                v-show="showLoginPassword"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#069bd7"
                width="17.433"
                height="15.57"
              >
                <path
                  d="M1.18164 12C2.12215 6.87976 6.60812 3 12.0003 3C17.3924 3 21.8784 6.87976 22.8189 12C21.8784 17.1202 17.3924 21 12.0003 21C6.60812 21 2.12215 17.1202 1.18164 12ZM12.0003 17C14.7617 17 17.0003 14.7614 17.0003 12C17.0003 9.23858 14.7617 7 12.0003 7C9.23884 7 7.00026 9.23858 7.00026 12C7.00026 14.7614 9.23884 17 12.0003 17ZM12.0003 15C10.3434 15 9.00026 13.6569 9.00026 12C9.00026 10.3431 10.3434 9 12.0003 9C13.6571 9 15.0003 10.3431 15.0003 12C15.0003 13.6569 13.6571 15 12.0003 15Z"
                ></path>
              </svg>
              <svg
                v-show="!showLoginPassword"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#069bd7"
                width="17.433"
                height="15.57"
              >
                <path
                  d="M4.52047 5.93457L1.39366 2.80777L2.80788 1.39355L22.6069 21.1925L21.1927 22.6068L17.8827 19.2968C16.1814 20.3755 14.1638 21.0002 12.0003 21.0002C6.60812 21.0002 2.12215 17.1204 1.18164 12.0002C1.61832 9.62282 2.81932 7.5129 4.52047 5.93457ZM14.7577 16.1718L13.2937 14.7078C12.902 14.8952 12.4634 15.0002 12.0003 15.0002C10.3434 15.0002 9.00026 13.657 9.00026 12.0002C9.00026 11.537 9.10522 11.0984 9.29263 10.7067L7.82866 9.24277C7.30514 10.0332 7.00026 10.9811 7.00026 12.0002C7.00026 14.7616 9.23884 17.0002 12.0003 17.0002C13.0193 17.0002 13.9672 16.6953 14.7577 16.1718ZM7.97446 3.76015C9.22127 3.26959 10.5793 3.00016 12.0003 3.00016C17.3924 3.00016 21.8784 6.87992 22.8189 12.0002C22.5067 13.6998 21.8038 15.2628 20.8068 16.5925L16.947 12.7327C16.9821 12.4936 17.0003 12.249 17.0003 12.0002C17.0003 9.23873 14.7617 7.00016 12.0003 7.00016C11.7514 7.00016 11.5068 7.01833 11.2677 7.05343L7.97446 3.76015Z"
                ></path>
              </svg>
            </button>
            <svg
              v-if="authError"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="authError"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ prettyAuthErrors[authError] }}</span
            >
          </div>
        </div>
      </div>
      <div class="mt-6 flex -mx-2 leading-xl">
        <div class="w-1/2 flex-grow pl-2 pr-4 flex content-start">
          <div class="text-left">
            <label
              for="remember_me"
              class="text-sml text-blue cursor-pointer align-middle"
            >
              <input
                id="remember_me"
                v-model="rememberMe"
                type="checkbox"
                name="remember_me"
                class="checkbox mr-2"
              />
              Keep me signed in on this device
            </label>
          </div>
        </div>
        <div class="w-1/2 flex pl-4 text-right justify-end">
          <div class="pl-3 pr-2">
            <router-link
              :to="{ name: 'login-by-phone' }"
              class="text-sm text-blue"
              >Login With My Cell Phone</router-link
            >
          </div>
          <div class="pl-2 pr-2 flex flex-col">
            <button
              class="primary-button inline-block"
              :disabled="isTryingToLogin"
              @click="login"
              ><span v-if="isTryingToLogin">Loading</span
              ><span v-if="!isTryingToLogin">Login</span></button
            >

            <router-link
              :to="{ name: 'forgot-password' }"
              class="text-sm text-blue ml-2 inline-block"
              >Forgot Password?</router-link
            >
          </div>
        </div>
      </div>
      <div class="flex items-center my-6">
        <div class="flex-grow border-t border-gray"></div>
        <span
          v-if="!signupVisible && !successSignup"
          class="px-4 text-gray-500 text-sm"
          >Or</span
        >
        <div class="flex-grow border-t border-gray"></div>
      </div>

      <div
        v-if="!signupVisible && !successSignup"
        class="flex justify-between items-center gap-3"
      >
        <button
          class="w-42 h-10 block bg-white text-sm text-blue leading-xl border border-blue border-solid rounded"
          @click="signupVisible = true"
          >Sign Up With Your Email</button
        >
        <div
          id="appleid-signin"
          data-color="white"
          data-border="true"
          data-type="sign in"
          data-width="160"
          data-height="40"
          class="cursor-pointer"
          @click="onAppleSuccess"
        ></div>
        <a
          class="h-10 px-4 bg-white text-sm border border-black border-solid rounded flex items-center gap-2 hover:bg-gray-50"
          href="/auth/microsoft/redirect"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 23 23"
          >
            <path fill="#f25022" d="M0 0h10.923v10.923H0z" />
            <path fill="#00a4ef" d="M12.077 0H23v10.923H12.077z" />
            <path fill="#7fba00" d="M0 12.077h10.923V23H0z" />
            <path fill="#ffb900" d="M12.077 12.077H23V23H12.077z" />
          </svg>
          <span>Sign in with Microsoft</span>
        </a>
        <div id="google-one-tap" class=""></div>
        <span
          v-if="authError && authError === 'provider_email_taken'"
          class="mt-1.5 mr-7 text-red text-xxs capitalize text-center block w-full"
          >{{ prettyAuthErrors[authError] }}</span
        >
      </div>
    </div>
    <div v-if="signupVisible" class="w-1/2 register-form mx-auto mt-10">
      <div class="relative">
        <input
          v-model="email"
          type="text"
          name="email"
          placeholder="Email"
          class="input"
          :class="[
            signUpError === 'email_taken' || errors.email !== ''
              ? 'border-red'
              : '',
          ]"
          @input="clearSignUpErrors('email')"
        />
        <svg
          v-if="signUpError === 'email_taken' || errors.email !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="signUpError === 'email_taken'"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ prettySignUpErrors[signUpError] }}</span
        >
        <span
          v-if="errors.email !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.email }}</span
        >
      </div>
      <div class="mt-7 relative">
        <input
          v-model="newPassword"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          class="input"
          placeholder="Password"
          :class="[authError ? 'border-red' : '']"
          @input="clearAuthErrors"
        />
        <button
          class="focus:outline-none mt-2.5 -ml-8 absolute"
          @click="toggleShowPassword"
        >
          <svg
            v-show="showPassword"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="#069bd7"
            width="17.433"
            height="15.57"
          >
            <path
              d="M1.18164 12C2.12215 6.87976 6.60812 3 12.0003 3C17.3924 3 21.8784 6.87976 22.8189 12C21.8784 17.1202 17.3924 21 12.0003 21C6.60812 21 2.12215 17.1202 1.18164 12ZM12.0003 17C14.7617 17 17.0003 14.7614 17.0003 12C17.0003 9.23858 14.7617 7 12.0003 7C9.23884 7 7.00026 9.23858 7.00026 12C7.00026 14.7614 9.23884 17 12.0003 17ZM12.0003 15C10.3434 15 9.00026 13.6569 9.00026 12C9.00026 10.3431 10.3434 9 12.0003 9C13.6571 9 15.0003 10.3431 15.0003 12C15.0003 13.6569 13.6571 15 12.0003 15Z"
            ></path>
          </svg>
          <svg
            v-show="!showPassword"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="#069bd7"
            width="17.433"
            height="15.57"
          >
            <path
              d="M4.52047 5.93457L1.39366 2.80777L2.80788 1.39355L22.6069 21.1925L21.1927 22.6068L17.8827 19.2968C16.1814 20.3755 14.1638 21.0002 12.0003 21.0002C6.60812 21.0002 2.12215 17.1204 1.18164 12.0002C1.61832 9.62282 2.81932 7.5129 4.52047 5.93457ZM14.7577 16.1718L13.2937 14.7078C12.902 14.8952 12.4634 15.0002 12.0003 15.0002C10.3434 15.0002 9.00026 13.657 9.00026 12.0002C9.00026 11.537 9.10522 11.0984 9.29263 10.7067L7.82866 9.24277C7.30514 10.0332 7.00026 10.9811 7.00026 12.0002C7.00026 14.7616 9.23884 17.0002 12.0003 17.0002C13.0193 17.0002 13.9672 16.6953 14.7577 16.1718ZM7.97446 3.76015C9.22127 3.26959 10.5793 3.00016 12.0003 3.00016C17.3924 3.00016 21.8784 6.87992 22.8189 12.0002C22.5067 13.6998 21.8038 15.2628 20.8068 16.5925L16.947 12.7327C16.9821 12.4936 17.0003 12.249 17.0003 12.0002C17.0003 9.23873 14.7617 7.00016 12.0003 7.00016C11.7514 7.00016 11.5068 7.01833 11.2677 7.05343L7.97446 3.76015Z"
            ></path>
          </svg>
        </button>
        <svg
          v-if="errors.password !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.password !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.password }}</span
        >
      </div>
      <div class="mt-7 relative">
        <input
          v-model="fullName"
          type="text"
          name="fullName"
          placeholder="Full Name"
          class="input"
          :class="[errors.name !== '' ? 'border-red' : '']"
          @input="clearSignUpErrors('name')"
        />
        <svg
          v-if="errors.name !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.name !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.name }}</span
        >
      </div>
      <div class="mt-7 text-center mb-12">
        <button
          class="primary-button"
          :disabled="isTryingToSignup"
          @click="signup"
          ><span v-if="isTryingToSignup">Loading</span
          ><span v-if="!isTryingToSignup">Sign Up</span></button
        >
      </div>
    </div>
    <div v-if="successSignup" class="success-message mx-auto mt-5">
      <div class="mt-5 text-basel text-black">
        You have succesfully signed up! Please check you email for the
        confirmation email!<br />
        <span class="text-sm"
          >(Account confirmation will expire in 24 hours)</span
        >
      </div>
    </div>
    <a
      href="https://projecthelping.org/privacy-policy/"
      target="_blank"
      class="text-sm text-blue fixed bottom-0 left-1/2 transform -translate-x-1/2 mb-4"
      >Privacy Policy</a
    >
  </div>
</template>
